/**
 * JWT 輔助函式
 *
 * 前端只做「過期與否」的本地判斷，不驗簽（驗簽是後端的事）。
 * 目的是避免拿著明顯已過期的 token 去打 API，換來一輪 401 → refresh → 登出 → 整頁跳轉。
 */

interface JwtPayload {
  exp?: number;
  [key: string]: unknown;
}

/**
 * 解碼 JWT payload（不驗簽）
 * @returns payload 物件；若不是合法的 JWT 則回傳 null
 */
export function decodeJwtPayload(token: string): JwtPayload | null {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  const encodedPayload = parts.length === 3 ? parts[1] : undefined;
  if (!encodedPayload) {
    return null;
  }

  try {
    // base64url → base64，並補上 padding
    const base64 = encodedPayload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');

    const json =
      typeof atob === 'function'
        ? decodeURIComponent(
            atob(padded)
              .split('')
              .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
              .join('')
          )
        : Buffer.from(padded, 'base64').toString('utf-8');

    const payload = JSON.parse(json);
    return payload !== null && typeof payload === 'object' ? (payload as JwtPayload) : null;
  } catch {
    return null;
  }
}

/**
 * 判斷 token 是否已過期
 *
 * 刻意保守：只有在「確定解得出 exp 且確定已過期」時才回傳 true。
 * 若 token 不是 JWT、或沒有 exp（不透明 token），一律視為「未過期」，
 * 交給後端用 401 來判定，避免前端誤判把使用者踢出去。
 *
 * @param token 要檢查的 token
 * @param leewaySeconds 提前多少秒視為過期，用來吸收前後端時鐘誤差與請求延遲
 */
export function isJwtExpired(token: string, leewaySeconds = 10): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.exp !== 'number') {
    return false;
  }
  return payload.exp * 1000 <= Date.now() + leewaySeconds * 1000;
}

/**
 * token 裡的組織 scope 與預期是否相符
 *
 * - `match`：token 帶了組織資訊，且就是預期的那個組織
 * - `mismatch`：token 帶了組織資訊，但是**別的**組織
 * - `unknown`：解不開，或 payload 裡認不出組織 uuid 欄位 —— 無從驗證
 */
export type JwtOrgScopeCheck = 'match' | 'mismatch' | 'unknown';

/**
 * 已知會帶「組織 uuid」的 claim 名稱。命中任何一個就以它們為準，不再往下猜。
 *
 * 刻意只收 uuid 類的欄位：`org_id` 這種數字型的識別碼拿去跟 uuid 比對必定不相等，
 * 會把每一次切組織都判成失敗。
 */
const KNOWN_ORG_UUID_CLAIM_KEYS = [
  'org_uuid',
  'orguuid',
  'organization_uuid',
  'organizations_uuid',
] as const;

/** 後端改過欄位名時的退路：任何含 org 的 key 都算候選 */
const ORG_CLAIM_KEY_PATTERN = /org/i;

/** uuid 形狀，用來從候選 claim 裡篩掉 `organizations_name` 這種同樣含 org 的欄位 */
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** payload 裡所有非空的字串欄位 */
const stringEntries = (payload: JwtPayload): Array<[string, string]> =>
  Object.entries(payload).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].length > 0
  );

/**
 * 檢查 token 的組織 scope 是否等於 `expectedOrgUuid`
 *
 * 用途是切換組織之後確認「換到的 token 真的是新組織的」：後端曾發生過
 * refresh 回來的 token 仍帶著舊組織 scope，前端只看「有沒有拿到 token」就當成功，
 * 於是畫面是新組織、每支 API 卻讀到上一個組織的資料。
 *
 * 刻意保守：只有在「確定 token 裡有組織欄位、且確定是別的組織」時才回 `mismatch`。
 * 認不出組織欄位一律回 `unknown` 交給呼叫端放行，避免後端改欄位名就把所有人踢出去。
 *
 * 不驗簽——這裡只是讀 payload 做一致性檢查，真正的信任邊界在後端。
 */
export function checkJwtOrgScope(token: string, expectedOrgUuid: string): JwtOrgScopeCheck {
  if (!expectedOrgUuid) return 'unknown';

  const payload = decodeJwtPayload(token);
  if (!payload) return 'unknown';

  const entries = stringEntries(payload);

  // 第一順位：已知的組織 uuid 欄位。有就以它為準。
  let orgValues = entries
    .filter(([key]) => KNOWN_ORG_UUID_CLAIM_KEYS.includes(key.toLowerCase() as never))
    .map(([, value]) => value);

  // 第二順位（後端改過欄位名）：含 org 的 key，但只採 uuid 形狀的值。
  //
  // 不篩形狀的話，`organizations_name`、`org_role` 這類同樣含 org 的欄位也會被當成
  // 組織 scope 拿去比對 —— 值必然不等於 uuid，於是每一次切組織／轉移擁有權都被判成
  // 失敗而強制重新登入。認錯欄位比認不出來嚴重得多，所以寧可退回 unknown。
  if (orgValues.length === 0) {
    orgValues = entries
      .filter(([key, value]) => ORG_CLAIM_KEY_PATTERN.test(key) && UUID_PATTERN.test(value))
      .map(([, value]) => value);
  }

  if (orgValues.length === 0) return 'unknown';

  return orgValues.some((value) => value.toLowerCase() === expectedOrgUuid.toLowerCase())
    ? 'match'
    : 'mismatch';
}
